import { useMemo, useState } from 'react';
import { App as AntApp, Button, Switch } from 'antd';
import SheetSelect from '../components/SheetSelect';
import AppBar from '../components/AppBar';
import InfoNotes from '../components/InfoNotes';
import { InfoDot, NumInput, MoneyInput } from './MortgagePage';
import { num } from '../lib/loanCalc';

// Tenancy — legal fee, stamp duty and move-in costs for a tenancy agreement.
// Stamp duty follows the reference tool's method: RM1 per RM250 of ANNUAL rent
// for terms ≤ 1 year (RM2 for 1–3 years, RM4 above 3), rounded up, plus RM10
// for one additional stamped copy. Legal fee (optional) per SRO 2023: 30% of
// one month's rent, minimum RM500 (rent ≤ RM10k/mo; 15% above that).

const fmt2 = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const DEFAULTS = {
  rent: '', agreementFee: '300', other: '',
  years: '1', securityMonths: '2', utilityMonths: '0.5',
  includeLawyer: false,
};

const YEAR_OPTS = ['1', '2', '3', '4', '5'].map((v) => ({ value: v, label: v }));
const SEC_OPTS = ['1', '1.5', '2', '2.5', '3'].map((v) => ({ value: v, label: v }));
const UTIL_OPTS = ['0.5', '1', '1.5', '2'].map((v) => ({ value: v, label: v }));

const stampRate = (years) => (num(years) <= 1 ? 1 : num(years) <= 3 ? 2 : 4);
const tenancyLegalFee = (rent) => {
  const m = num(rent);
  if (m <= 0) return 0;
  return m <= 10000 ? Math.max(500, m * 0.3) : m * 0.15;
};

export default function TenancyPage() {
  const { message } = AntApp.useApp();
  const [f, setF] = useState(DEFAULTS);
  const [showResult, setShowResult] = useState(false);
  const patch = (p) => setF((x) => ({ ...x, ...p }));

  const r = useMemo(() => {
    const rent = num(f.rent);
    const annual = rent * 12;
    const stamp = rent > 0 ? Math.ceil(annual / 250) * stampRate(f.years) + 10 : 0;
    const legal = f.includeLawyer ? tenancyLegalFee(rent) : 0;
    const security = rent * num(f.securityMonths);
    const utility = rent * num(f.utilityMonths);
    const agreement = num(f.agreementFee);
    const other = num(f.other);
    return {
      rent, stamp, legal, security, utility, agreement, other,
      // Total includes the advance rental (first month's rent) — reference note 1.
      total: rent + legal + security + utility + agreement + other + stamp,
    };
  }, [f]);

  const [infoOpen, setInfoOpen] = useState(false);
  const info = () => setInfoOpen(true);
  const INFO_PAGES = [
    { body: 'Calculate legal fee, stamp duty and others for Tenancy Agreement.' },
    { title: 'Deposits', body: '1. Total is inclusive of advance rental (first month\u2019s rental).\n\n2. Earnest Deposit: equivalent to 1 month\u2019s rent \u2014 money paid to reserve the unit, usually utilised as the first month\u2019s rental.\n\n3. Security Deposit: typically 2 months\u2019 rent \u2014 a bond waived in favour of the landlord if the tenant breaches the agreement; refunded in full at the end of a clean tenancy.\n\n4. Utility Deposit: typically 50% of 1 month\u2019s rent \u2014 covers unpaid utility bills at the end of the tenancy.' },
    { title: 'Fees & duty', body: '5. Security and Utility Deposits are paid to the landlord upon signing the Tenancy Agreement.\n\n6. Agreement Fee: defaulted to RM300 but editable. 7. Other: any additional fee.\n\n8. Lawyer fee (optional) per SRO 2023: 30% of one month\u2019s rent, minimum RM500 (monthly rent \u2264 RM10k).\n\n* Stamp duty is inclusive of one additional copy of stamping (RM10).' },
  ];

  const copyResult = async () => {
    const text = [
      'Tenancy',
      `Monthly Rental: ${fmt2(r.rent)}`,
      `Tenancy (Year): ${num(f.years)}`,
      '',
      `Legal Fee: ${fmt2(r.legal)}`,
      `Security Deposit: ${fmt2(r.security)}`,
      `Utility Deposit: ${fmt2(r.utility)}`,
      `Agreement Fee: ${fmt2(r.agreement)}`,
      `Other: ${fmt2(r.other)}`,
      `Stamp Duty: ${fmt2(r.stamp)}`,
      `Total: RM${fmt2(r.total)}`,
    ].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      message.success('Result copied to clipboard');
    } catch (_) {
      message.error('Could not copy — long-press the summary to copy manually');
    }
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar back title="Tenancy" right={<InfoDot onClick={info} />} />
      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label="Monthly Rental">
          <MoneyInput value={f.rent} onChange={(v) => patch({ rent: v })} placeholder="0" />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Agreement Fee">
            <MoneyInput value={f.agreementFee} onChange={(v) => patch({ agreementFee: v })} placeholder="300" />
          </Field>
          <Field label="Other">
            <MoneyInput value={f.other} onChange={(v) => patch({ other: v })} placeholder="0" />
          </Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <Field label="Tenancy (Year)">
            <SheetSelect title="Tenancy term (years)" value={f.years} onChange={(v) => patch({ years: v })} options={YEAR_OPTS} />
          </Field>
          <Field label="Security Deposit">
            <SheetSelect title="Security deposit (months)" value={f.securityMonths} onChange={(v) => patch({ securityMonths: v })} options={SEC_OPTS} />
          </Field>
          <Field label="Utility Deposit">
            <SheetSelect title="Utility deposit (months)" value={f.utilityMonths} onChange={(v) => patch({ utilityMonths: v })} options={UTIL_OPTS} />
          </Field>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <span style={{ fontSize: 13 }}>Include Lawyer Fee</span>
          <Switch checked={f.includeLawyer} onChange={(v) => patch({ includeLawyer: v })} />
        </div>

        {showResult && r.rent > 0 && (
          <>
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
              <div style={{ padding: '10px 14px 4px', fontSize: 12.5, fontWeight: 700, letterSpacing: '.04em', color: 'var(--primary)' }}>SUMMARY</div>
              <Row label="Monthly Rental" value={fmt2(r.rent)} />
              {f.includeLawyer && <Row label="Legal Fee" value={fmt2(r.legal)} />}
              <Row label="Security Deposit" value={fmt2(r.security)} />
              <Row label="Utility Deposit" value={fmt2(r.utility)} />
              <Row label="Agreement Fee" value={fmt2(r.agreement)} />
              <Row label="Other" value={fmt2(r.other)} />
              <Row label="Stamp Duty*" value={fmt2(r.stamp)} />
              <Row label="Total (RM)" value={fmt2(r.total)} strong />
            </div>
            <Button block size="large" type="primary" onClick={copyResult}>Copy Result</Button>
          </>
        )}
      </div>

      <InfoNotes open={infoOpen} onClose={() => setInfoOpen(false)} title="Tenancy" pages={INFO_PAGES} />

      <div style={{ display: 'flex', gap: 12, padding: '12px 12px max(12px, env(safe-area-inset-bottom))', borderTop: '1px solid var(--border)', background: 'var(--surface)', flex: 'none' }}>
        <Button block size="large" onClick={() => { setF(DEFAULTS); setShowResult(false); }}>Reset</Button>
        <Button block size="large" type="primary" onClick={() => (num(f.rent) > 0 ? setShowResult(true) : message.warning('Enter the monthly rental'))}>Calculate</Button>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    <label style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--muted)' }}>{label}</label>
    {children}
  </div>
);

const Row = ({ label, value, strong }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, padding: '14px 16px', borderTop: '1px solid var(--border)', fontSize: 13, background: strong ? 'var(--chip)' : undefined }}>
    <span style={{ fontWeight: strong ? 600 : 400, color: strong ? 'var(--primary)' : 'var(--text)' }}>{label}</span>
    <b style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color: strong ? 'var(--primary)' : 'var(--text)' }}>{value}</b>
  </div>
);
